/* eslint-disable react/prop-types */
import React, { useRef, useMemo, useState, useCallback, useEffect, useImperativeHandle } from 'react';
import classnames from 'classnames';
import { history } from 'umi';
import * as rbd from 'react-beautiful-dnd';
import { Table, notification } from 'tea-component';
import { cloneDeep } from 'lodash-es';
import FixedBottomScrollbar from '@home/components/FixedBottomScrollbar';
import GeneralModal from '@gp/components/GeneralModal';
import TinyEditor from '@gp/components/TinyEditor';
import ProjectUpdateCard from '@gp/pages/Pipeline/ProjectDetails/ProjectUpdate/components/ProjectUpdateCard';
import { formatGRActionItemsRes } from '@gp/pages/Pipeline/ProjectDetails/ProjectUpdate/utils';
import PPSCommentDetails from './components/PPSCommentDetails';
import PPSCommentEdit from './components/PPSCommentEdit';
import UpdateAllProjectForm from './components/UpdateAllProjectForm';
import useTableStatus from '@gp/hooks/useTableStatus';
import useReviewOrEditModal from '@gp/hooks/useReviewOrEditModal';
import useFirstTimeChangeHistoriesEffect from './_hooks/useFirstTimeChangeHIstoriesEffect';
import { projectListExecuteEdit } from '@gp/services/project';
import { messageTips, getNumberOfDotUnit } from '@gp/utils/common';
import { goToProjectDetailsPage } from '@gp/utils/project';
import { getColumns } from './utils/common';
import { PageMode } from '@gp/enums/common';
import { MENU_ID_MAP, SLASH_G_POP } from '@gp/constants/const';

import '../../index.scss';
import './index.scss';

const { sortable, pageable, scrollable, draggable } = Table.addons;

// 提示编辑成功或者失败
const totip = (isSuc) => {
  isSuc ? messageTips('success', 'Save success') : messageTips('error', 'Save failed. Please check the network');
  return isSuc;
};

/**
 * 获取取表格数据的下标索引
 * @param {number} pageIndex 页码
 * @param {number} pageSize 页长
 * @param {number} recordCount 数据总数
 * @returns 开始索引及结束索引
 */
const getIndex = (pageIndex, pageSize, recordCount) => {
  const startIndex = (pageIndex - 1) * pageSize;
  const endIndex = pageIndex * pageSize > recordCount ? recordCount - 1 : pageIndex * pageSize - 1;
  return {
    startIndex,
    endIndex,
  };
};

const ProjectList = (props, ref) => {
  const {
    againRequest,
    tableProps,
    isReport,
    tableList,
    ppsViewable = false,
    updatesViewable = false,
    ppsViewVisible = true,
    ppsEditable = false,
    setLockerData,
    handleReset,
    scrollbarHeight,
  } = props;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const tableRef = useRef(null);
  const PDFTableContainerRef = useRef(null);
  const [keyQuestionPureText, setKeyQuestionPureText] = useState('');
  const [keyQuestionContent, setKeyQuestionContent] = useState('');
  // const [keyUpdatesVisible, setKeyUpdatesVisible] = useState(false);
  const [projectUpdatesVisible, setProjectUpdatesVisible] = useState(false);
  const [projectUpdatesModalList, setProjectUpdatesModalList] = useState(null);
  const projectUpdatesModalListNew = useMemo(() => {
    const { gr_action: grActions, ...restProjectUpdatesModalList } = projectUpdatesModalList || {};
    return { ...restProjectUpdatesModalList, gr_action: formatGRActionItemsRes(grActions) };
  }, [projectUpdatesModalList]);
  const [allProjectEditModalVisible, setAllProjectEditModalVisible] = useState(false);
  // 编辑态时当前编辑项编辑后的数据，也可能是编辑前的数据（比如：编辑key updates时）
  const [currentEditItem, setCurrentEditItem] = useState(null);
  // 编辑态时编辑过的所有数据
  const [editList, setEditList] = useState([]);
  // 表格数据
  const [records, setRecords] = useState([]);
  // 表格分页展示数据，受拖拽功能影响分页插件失效，所以表格展示的数据模拟后端分页效果
  const [pageableRecords, setPageableRecords] = useState([]);
  // 进入编辑态前的数据
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [beforeRecords, setBeforeRecords] = useState([]);
  const [isWangEditorFocused, setIsWangEditorFocused] = useState(false);
  const [reportUpdatesViewable, setReportUpdatesViewable] = useState(false);

  // pps comment 编辑、弹窗相关
  const {
    modalMode: ppsModalMode,
    modalTitle: ppsModalTitle,
    modalVisible: ppsModalVisible,
    modalData: ppsModalData,
    setModalData: ppsSetModalData,
    openModal: ppsOpenModal,
    closeModal: ppsCloseModal,
  } = useReviewOrEditModal({
    reviewModalTitle: 'DPS Comment',
    editModalTitle: 'DPS Comment Edit',
    defaultModalData: {
      list: null,
      view_pps: false,
    },
  });

  const firstTimeChangeHistoriesData = useFirstTimeChangeHistoriesEffect(records);

  const scrollbarDeps = useMemo(
    () => [isReport, isEditStatus, ppsViewable, ppsViewVisible, reportUpdatesViewable, updatesViewable],
    [isReport, isEditStatus, ppsViewable, ppsViewVisible, reportUpdatesViewable, updatesViewable],
  );

  const { search, filterValues, searchValues, refresh } = againRequest;
  const {
    sorter,
    pagination,
    dataSource,
    error,
    loading,
    // tableMaxH,
    isEditStatus,
    projectListTableLoading,
    setProjectListTableLoading,
  } = tableProps;

  useEffect(() => {
    setProjectListTableLoading?.(false);
    setRecords(dataSource);
  }, [dataSource]);

  useEffect(() => {
    if (isReport && tableList?.length) {
      setRecords(tableList);
      const reportUpdatesViewable = tableList?.some((item) => item?.updates_info?.view_update);
      setReportUpdatesViewable(reportUpdatesViewable);
    }
  }, [isReport, tableList]);

  const { sorts, onSortsChange } = sorter;
  const { pageIndex, pageSize, recordCount, onPagingChange } = pagination;
  const open = useCallback((cvm) => {
    // setKeyQuestionContent(cvm.key_question);
    setKeyQuestionContent(cvm?.updates_info?.content?.key_updates);
    setKeyQuestionPureText(cvm?.updates_info?.content?.key_updates?.replace(/<[^>]+>/gi, ''));
    // setKeyUpdatesVisible(true);
  }, []);

  const toDetails = useCallback((item, e, isJump = true) => {
    if (!item?.project_id) return '';
    const filter = encodeURIComponent(
      JSON.stringify({
        project: item?.project,
        projectId: item?.project_id,
        menuFrom: MENU_ID_MAP['pipeline_all-projects'], // 用于标识左侧菜单栏当前选中高亮
      }),
    );
    const url = `${SLASH_G_POP}/Pipeline/ProjectList/Detail?filter=${filter}`;
    if (isJump) {
      e?.preventDefault();
      e?.stopPropagation();
      history.push(url);
    }

    return url;
  }, []);

  const operat = (flag) => {
    if (flag === 'back') {
      const { form } = search;
      for (const key in filterValues) {
        if (key !== 'region') {
          form.change(key, filterValues[key]);
        }
      }
      // 点击back时，筛选器全局状态修改
      setLockerData?.({ filter: filterValues });
      search.submit({ ...filterValues, ...searchValues, is_reset: true });
    } else if (flag === 'reset') {
      handleReset();
    } else {
      // 超时
      refresh();
    }
  };

  const tableStatus = useTableStatus({
    loading,
    error,
    dataSource: isReport ? tableList : dataSource,
    operat,
    isUseResetNodata: true,
  });

  // 拖拽完成回调
  const onDragEnd = useCallback(
    (rec, con) => {
      if (Array.isArray(rec)) {
        setPageableRecords(rec); // 这里不提前设置数据项会抖动渲染
        const newRecords = [...records];
        newRecords.splice((pageIndex - 1) * pageSize, pageSize, ...rec);
        setRecords(newRecords);
        // 拖拽项原始位置和终点位置
        const start = con?.source?.index;
        const end = con?.destination?.index;
        if (start === end) return;
        // 位置发送变化的起始和结束索引
        const startIndex = start > end ? end : start;
        const endIndex = start > end ? start : end;
        // 位置发送变化的数据
        const changePositionList = [];
        for (let i = 0; i < rec?.length; i++) {
          const t = rec?.[i];
          if (i >= startIndex && i <= endIndex) {
            changePositionList.push({
              ...t,
              position: i + (pageIndex - 1) * pageSize + 1, // 标识变化后的位置
            });
          }
        }
        // 过滤掉编辑过的数据中已经存在在changePositionList中的重复数据
        const filterEditList = editList?.length
          ? editList?.filter((t) => {
              let isRepeat = false;
              for (let i = 0; i < changePositionList?.length; i++) {
                const item = changePositionList?.[i];
                if (item?.project_id === t?.project_id) isRepeat = true;
              }
              return !isRepeat;
            })
          : editList;
        // 把拖拽位置发送变化的数据加到editList中
        const newEditList = filterEditList?.length ? [...filterEditList, ...changePositionList] : changePositionList;
        setEditList(newEditList);
      }
    },
    [records, pageIndex, pageSize, editList],
  );

  const pageSizeOptions = [20, 50, 100];
  const showPageableOperation = recordCount > Math.min(...pageSizeOptions);
  // 表格使用到的插件
  const addons = [
    draggable({
      module: rbd,
      onDragEnd,
    }),
    // pageable({
    //   stateTextVisible: true,
    //   pageSizeVisible: false,
    //   pageIndexVisible: false,
    //   jumpVisible: false,
    //   endJumpVisible: false,
    //   pageIndex: 1,
    //   pageSize: 20,
    //   pageSizeOptions,
    //   recordCount,
    //   stateText: `${getNumberOfDotUnit(recordCount)} item${recordCount > 1 ? 's' : ''} in total`,
    // }),
    pageable({
      stateTextVisible: true,
      pageSizeVisible: showPageableOperation,
      pageIndexVisible: showPageableOperation,
      jumpVisible: showPageableOperation,
      endJumpVisible: showPageableOperation,
      pageIndex,
      pageSize,
      pageSizeOptions,
      recordCount,
      onPagingChange,
      stateText: `${getNumberOfDotUnit(recordCount)} item${recordCount > 1 ? 's' : ''} in total`,
    }),
    sortable({
      columns: !isEditStatus && [
        {
          key: 'status',
          prefer: 'desc',
        },
        'studio',
        'priority',
        'business_owner',
        'launch_date',
        'next_checkpoint',
        {
          key: 'update_time',
          prefer: 'desc',
        },
      ],
      value: sorts,
      onChange: onSortsChange,
    }),
    scrollable({
      // maxHeight: isReport ? undefined : tableMaxH || 500,
      // minHeight: tableMaxH || 500,
      minWidth: isReport ? 1150 : 1715,
    }),
    tableStatus,
  ];

  if (isReport) {
    addons.splice(0, 3);
  } else {
    // 非编辑态，不需要拖拽排序
    if (!isEditStatus) addons.shift();
  }

  // 模拟数据分页，表格展示的数据
  useEffect(() => {
    if (!records?.length || !pageIndex || !pageSize || !recordCount) {
      setPageableRecords([]);
      return;
    }
    const { startIndex, endIndex } = getIndex(pageIndex, pageSize, recordCount);
    const pageableRecords = records?.filter((_, i) => i >= startIndex && i <= endIndex);
    setPageableRecords(pageableRecords);
  }, [records, pageIndex, pageSize, recordCount]);

  // 触发分页时通知父组件拖拽按钮重新绑定鼠标按下事件
  useEffect(() => {
    if (records?.length) {
      props.setToAddMouseDown?.(Date.now());
    }
  }, [records, pageIndex]);

  // 编辑后变化的数据反应到records，并添加到editList中
  useEffect(() => {
    if (!currentEditItem) return;

    // 添加editList数据
    const newEditList = [...editList];
    const index = newEditList.findIndex((t) => t?.project_id === currentEditItem?.project_id);
    if (index === -1) {
      // 没有重复数据
      setEditList([...newEditList, currentEditItem]);
    } else if (index !== -1 && !Number.isInteger(newEditList?.[index]?.position)) {
      // 有重复数据且重复的数据没有发生位置改变
      newEditList.splice(index, 1);
      setEditList([...newEditList, currentEditItem]);
    } else if (index !== -1 && Number.isInteger(newEditList?.[index]?.position)) {
      // 有重复数据且重复的数据发生位置改变
      const position = newEditList?.[index]?.position;
      newEditList.splice(index, 1);
      setEditList([...newEditList, { ...currentEditItem, position }]);
    }

    // 改变表格数据
    const newRecords = [...records];
    newRecords.forEach((t, i) => {
      if (t?.project_id === currentEditItem?.project_id) {
        newRecords.splice(i, 1, currentEditItem);
      }
    });
    setRecords(newRecords);
  }, [currentEditItem]);

  // 调用编辑接口
  const toEdit = useCallback(async (params) => {
    const res = await projectListExecuteEdit(params);
    if (res?.status === 200 && res?.data?.code === 200) {
      setEditList([]);
      refresh();
      return totip(true);
    }

    return totip(false);
  }, []);

  // return告知父组件是否退出编辑态
  const onSubmitResult = useCallback(async () => {
    if (!editList.length) return totip(true);
    const editedList = [];
    const positionList = []; // 编辑过数据的数据/位置发生变化的数据
    for (let ii = 0; ii < editList?.length; ii++) {
      const it = editList?.[ii];
      let isChange = false; // 数据是否发生改变
      for (let i = 0; i < beforeRecords?.length; i++) {
        const t = beforeRecords?.[i];
        if (t?.project_id === it?.project_id) {
          isChange =
            t?.priority !== it?.priority ||
            t?.launch_date !== it?.launch_date ||
            // || t?.key_question !== it?.key_question;
            t?.pps_comment?.list?.[0]?.status !== it?.pps_comment?.list?.[0]?.status ||
            t?.pps_comment?.list?.[0]?.comment !== it?.pps_comment?.list?.[0]?.comment ||
            t?.updates_info?.content?.key_updates !== it?.updates_info?.content?.key_updates;
          if (isChange) editedList.push(it);
          // 判断是否有position字段，判断位置是否真正发送变化
          if (Number.isInteger(it?.position) && it?.position !== i + 1) positionList.push(it);
        }
      }
    }

    if (editedList?.length === 0 && positionList?.length === 0) return totip(true);

    const params = {
      field_edit: editedList?.length
        ? editedList?.map((t) => {
            const keyUpdates = t?.updates_info?.content?.key_updates;
            const risks = t?.updates_info?.content?.risks;
            const workplans = t?.updates_info?.content?.workplans;
            const content = {
              key_updates: keyUpdates || '',
              risks: Array.isArray(risks) ? risks : [],
              workplans: Array.isArray(workplans) ? workplans : [],
            };
            const updateInfo = {
              project_id: t?.project_id ?? '',
              id: t?.updates_info?.id ?? '',
              // milestone: t?.current_milestone || '',
              // milestone_id: t?.current_milestone_id || '',
              progress: t?.progress ? Number(t?.progress) : null,
              content: JSON.stringify(content),
              date: t?.updates_info?.date || '',
              update_id: t?.updates_info?.update_id,
            };
            const ppsDataTemp = {
              project_id: t?.project_id,
              status: t?.pps_comment?.list?.[0]?.status || '0',
              comment: t?.pps_comment?.list?.[0]?.comment || '',
            };
            // pps_comment和update_info这两个字段如果没有编辑（编辑无变化），那这两个字段不传给后端
            let isUpadtePPSComment = false;
            let isUpdateProjectUpdates = false;
            if (Array.isArray(dataSource) && dataSource?.length) {
              for (let index = 0; index < dataSource?.length; index++) {
                const item = dataSource?.[index];
                if (item?.project_id === t?.project_id) {
                  isUpadtePPSComment =
                    item?.pps_comment?.list?.[0]?.status !== t?.pps_comment?.list?.[0]?.status ||
                    item?.pps_comment?.list?.[0]?.comment !== t?.pps_comment?.list?.[0]?.comment;
                  isUpdateProjectUpdates = item?.updates_info?.content?.key_updates !== keyUpdates;
                  break;
                }
              }
            }

            const res = {
              project: t?.project || '',
              priority: t?.priority || '0',
              launch_date: t?.launch_date || '',
              // key_question: t?.key_question || '',
              // pps_comment: ppsDataTemp,
              // update_info: updateInfo,
              project_id: t?.project_id,
              // current_milestone: t?.current_milestone || '',
              // current_milestone_id: t?.current_milestone_id || '',
            };
            if (isUpadtePPSComment) {
              res.pps_comment = ppsDataTemp;
            }
            if (isUpdateProjectUpdates) {
              res.update_info = updateInfo;
            }

            return res;
          })
        : [],
      /* second_sort: positionList?.length ? positionList?.map((t) => ({
        project_id: t?.project_id,
        position: t?.position,
      })) : [], */
      // 现在改成传全部位置给后端
      second_sort: positionList?.length
        ? records?.map((t, i) => ({
            project_id: t?.project_id,
            position: i + 1,
          }))
        : [],
    };

    return toEdit(params);
  }, [beforeRecords, editList, records, dataSource]);

  const onCancel = useCallback(() => {
    setRecords(beforeRecords);
    setEditList([]);
  }, [beforeRecords]);

  const getPDFtableContainer = () => PDFTableContainerRef.current;

  // 暴露给父组件的方法或属性
  useImperativeHandle(
    ref,
    () => ({
      getSubmitResult: onSubmitResult,
      onCancel,
      editList,
      getPDFtableContainer,
    }),
    [beforeRecords, editList, records, dataSource],
  );

  // const [aaHeight, setAaHeight] = useState(0);

  // useEffect(() => {
  //   const handleResize = () => {
  //     const hValue = window.innerHeight;
  //     setAaHeight(hValue * 0.4374);
  //   };
  //   handleResize();
  //   window.addEventListener('resize', handleResize);
  //   return () => window.removeEventListener('resize', handleResize);
  // }, []);

  const isReady = useMemo(() => {
    return loading || projectListTableLoading || Boolean(error);
  }, [loading, projectListTableLoading, error]);

  return (
    <>
      <div className={'dbp-project-list-table-content'}>
        <Table
          ref={tableRef}
          // className="dbg-projectlist-table projectlist__deleteBorder dtb-table-fixed-left"
          className={classnames({
            'dbg-common-table dbg-projectlist-table sort-icon-color dbg-table-sticky dbh-action-state-center-table dbg-table-hide-scrollbar dbg-projectlist-table-calc-overflow':
              true,
            'edit-status-table': isEditStatus,
          })}
          // style={styleCur}
          // verticalTop
          records={isReady ? [] : pageableRecords || []}
          recordKey={(record) => record.id}
          columns={getColumns(
            isEditStatus ? 'edit' : 'default',
            toDetails,
            open,
            setCurrentEditItem,
            isReport,
            setProjectUpdatesVisible,
            setProjectUpdatesModalList,
            setAllProjectEditModalVisible,
            ppsOpenModal,
            ppsViewable,
            ppsViewVisible,
            isReport ? reportUpdatesViewable : updatesViewable,
            ppsEditable,
            false,
            firstTimeChangeHistoriesData,
          )}
          addons={addons}
        />
        {!isReady && (
          <FixedBottomScrollbar
            style={{ bottom: scrollbarHeight || 89 }}
            deps={scrollbarDeps}
            getScrollableEl={() => {
              const tableEl = tableRef.current;
              return tableEl?.querySelector('.tea-table__body');
            }}
          />
        )}
      </div>

      {/* Project Updates弹窗 */}
      <GeneralModal
        className={classnames('dbg-project-updates-form-modal', {
          'dbg-all-projects-list-project-updates-modal globalpedia-custom-scrollbar gp-inital-list-style': true,
          'dbg-all-projects-list-project-updates-modal-no-scroll': isEditStatus,
        })}
        visible={projectUpdatesVisible}
        title="Project Updates"
        // isFont14
        isFont14={false}
        modalSize={1000}
        customFooterButtonGroup={{
          viewDetails: {
            type: 'primary',
            text: 'View Project Updates',
            ClickFn: () => {
              const { project_id: projectId, project: projectName } = projectUpdatesModalListNew || {};
              goToProjectDetailsPage({
                id: projectId,
                name: projectName,
                tab: 'projectUpdate',
              });
            },
          },
        }}
        content={
          isEditStatus ? (
            <TinyEditor
              className={`gp-word-break-word ${isWangEditorFocused ? 'gp-blue-border' : ''}`}
              status={!keyQuestionContent?.length || keyQuestionPureText?.length ? 'success' : 'error'}
              placeholder="Please input Project Updates"
              errorTips="Please input Project Updates"
              value={keyQuestionContent}
              getIsFocused={(v) => setIsWangEditorFocused(v)}
              getEditorContent={(v) => {
                setKeyQuestionContent(v);
              }}
              getEditorText={(v) => {
                setKeyQuestionPureText(v?.trim());
              }}
            />
          ) : (
            <ProjectUpdateCard type="default" itemList={projectUpdatesModalListNew} userIsAbleEdit={false} />
          )
        }
        footerButtonGroup={isEditStatus ? ['confirm', 'cancel'] : ['viewDetails', 'close']}
        disabledConfirm={!keyQuestionPureText?.length}
        onConfirm={() => {
          setIsWangEditorFocused(false);
          const newItem = cloneDeep(currentEditItem);
          try {
            newItem.updates_info.content.key_updates = keyQuestionContent;
          } catch (exception) {
            console.warn(exception);
          }
          setCurrentEditItem(newItem);
          setProjectUpdatesVisible(false);
        }}
        onClose={() => {
          setIsWangEditorFocused(false);
          setProjectUpdatesVisible(false);
        }}
      />

      {/* PSS Comment弹窗 */}
      <GeneralModal
        className={classnames({
          'dbg-all-projects-list-pps-comment-modal globalpedia-custom-scrollbar gp-inital-list-style': true,
          'dbg-all-projects-list-pps-comment-edit-modal': ppsModalMode === PageMode.Edit,
        })}
        visible={ppsModalVisible}
        title={ppsModalTitle}
        // isFont14
        isFont14={false}
        modalSize={800}
        content={
          ppsModalMode === PageMode.Edit ? (
            <PPSCommentEdit
              text={ppsModalData?.list?.[0]?.comment}
              onChange={(payload) => {
                const newPPSCommentList = Array.isArray(ppsModalData?.list)
                  ? ppsModalData?.list?.map((t, i) => (i === 0 ? { ...t, comment: payload || '' } : t))
                  : null;
                const ppsModalDataTemp = Array.isArray(newPPSCommentList)
                  ? {
                      ...ppsModalData,
                      list: newPPSCommentList,
                    }
                  : {
                      ...ppsModalData,
                      list: [
                        {
                          id: '',
                          comment: payload,
                          pps_update_time: '',
                          status: '',
                          update_user: '',
                        },
                      ],
                    };

                ppsSetModalData?.(ppsModalDataTemp);
              }}
            />
          ) : (
            <PPSCommentDetails data={ppsModalData} />
          )
        }
        footerButtonGroup={ppsModalMode === PageMode.Edit ? ['confirm', 'cancel'] : ['close']}
        onConfirm={() => {
          const currentEditItemTemp = {
            ...currentEditItem,
            pps_comment: {
              ...ppsModalData,
            },
          };
          setCurrentEditItem?.(currentEditItemTemp);
          ppsCloseModal?.();
        }}
        onClose={ppsCloseModal}
      />

      {/* Project Updates编辑页弹窗 */}
      <GeneralModal
        visible={allProjectEditModalVisible}
        isFont14
        title="Update Info"
        className="dbg-all-project-edit-modal"
        modalSize="auto"
        content={<UpdateAllProjectForm />}
        isFooter
        onConfirm={() => {
          notification.success({
            description: 'Save successful!',
          });
        }}
        onClose={() => {
          setAllProjectEditModalVisible(false);
        }}
      />
    </>
  );
};

export default React.memo(React.forwardRef(ProjectList));
